import axios from 'axios';

import { LOCAL_STORAGE_KEY_ACCESS_TOKEN } from 'src/services/constants/LocalStorage';
import { ModulesApiRoutes } from '../drivers';

const extractEnvelopeMessage = (payload) => {
    const message = payload?.message;

    if (typeof message === 'string' && message) {
        return message;
    }

    if (message?.description) {
        return message.description;
    }

    if (payload?.error && typeof payload.error === 'string') {
        return payload.error;
    }

    return 'Request failed';
};

const unwrapEnvelope = (response) => {
    const payload = response?.data;

    if (payload && payload.ok === false) {
        const error = new Error(extractEnvelopeMessage(payload));
        error.response = { data: payload };
        throw error;
    }

    return payload;
};

const unwrapSettings = (response) => {
    const payload = unwrapEnvelope(response);

    if (payload?.data !== undefined && !Array.isArray(payload.data)) {
        return payload.data;
    }

    return payload;
};

export default class MailerSettingsService {
    static instances = {};

    constructor(baseUrl) {
        this.baseUrl = baseUrl;
        this.resource = `${baseUrl}/mailer_setting`;
    }

    static getInstance(baseUrl) {
        const resolved = baseUrl || ModulesApiRoutes.gd.API_BASE_URL;

        if (!MailerSettingsService.instances[resolved]) {
            MailerSettingsService.instances[resolved] = new MailerSettingsService(resolved);
        }

        return MailerSettingsService.instances[resolved];
    }

    getAuthHeaders(includeJson = false) {
        const headers = {
            Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN)}`,
        };

        if (includeJson) {
            headers['Content-Type'] = 'application/json';
        }

        return headers;
    }

    async fetchMailerSettings() {
        const response = await axios.get(this.resource, {
            headers: this.getAuthHeaders(),
        });

        return unwrapSettings(response);
    }

    async fetchPresets() {
        const response = await axios.get(`${this.resource}/presets`, {
            headers: this.getAuthHeaders(),
        });

        return unwrapSettings(response);
    }

    async saveMailerSettings(settings) {
        const response = await axios.post(`${this.resource}/edit`, settings, {
            headers: this.getAuthHeaders(true),
        });

        return unwrapEnvelope(response);
    }

    async toggleMailerActivation(isActive) {
        const response = await axios.post(
            `${this.resource}/toggle_active`,
            typeof isActive === 'object' ? isActive : { isActive },
            {
                headers: this.getAuthHeaders(true),
            }
        );

        return unwrapEnvelope(response);
    }

    async testConnection(payload) {
        const response = await axios.post(`${this.resource}/validate`, payload, {
            headers: this.getAuthHeaders(true),
        });

        return unwrapEnvelope(response);
    }

    async startMicrosoftOAuth() {
        const response = await axios.get(`${this.resource}/oauth/microsoft/start`, {
            headers: this.getAuthHeaders(),
        });
        const payload = unwrapEnvelope(response);

        return payload?.data || payload;
    }

    async disconnectMicrosoftOAuth() {
        const response = await axios.post(
            `${this.resource}/oauth/microsoft/disconnect`,
            {},
            {
                headers: this.getAuthHeaders(true),
            }
        );

        return unwrapEnvelope(response);
    }

    async validateIncoming(payload = {}) {
        const response = await axios.post(
            `${this.resource}/validate_incoming`,
            payload,
            {
                headers: this.getAuthHeaders(true),
            }
        );

        return unwrapEnvelope(response);
    }
}
